import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import usePageMeta from "@/hooks/usePageMeta";

const hotKeywords = ["雨夜之诗", "韩国", "文艺", "悬疑", "短片", "都市"];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get("q") ?? "").trim();
  const [keyword, setKeyword] = useState(query);

  usePageMeta(
    query ? `搜索「${query}」- 日韩黄色电影` : "站内搜索 - 日韩黄色电影",
    query
      ? `在日韩黄色电影站内搜索「${query}」，查看相关日韩情色电影的封面、年份与题材信息。`
      : "日韩黄色电影站内搜索：支持按影片名称、导演、演员、地区与题材标签检索日韩影视作品。",
    "日韩黄色电影,影片搜索,日韩情色电影,影视检索",
  );

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => {
    if (!query) return [];
    const lower = query.toLowerCase();
    return movies.filter((movie) => {
      const haystack = [
        movie.title,
        movie.titleEn,
        movie.director,
        movie.region,
        ...movie.cast,
        ...movie.genres,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [query]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-8 md:pt-12 pb-16 md:pb-24 bg-background-50">
        <div className="w-full max-w-[1200px] mx-auto">
          <nav className="flex items-center gap-1.5 text-[11px] md:text-xs text-foreground-500">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-500">搜索</span>
          </nav>

          <h1 className="mt-5 font-heading text-2xl md:text-4xl text-foreground-950">站内搜索</h1>
          <p className="mt-2 text-xs md:text-sm text-foreground-500">
            支持按影片名称、导演、演员、地区与题材标签进行关键词检索。
          </p>

          <form onSubmit={submit} className="mt-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入影片名、导演或标签，例如「雨夜」「韩国」「文艺」"
                className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-background-300/70 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-500 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-md bg-primary-500 text-foreground-950 text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-search-line" />
              搜索影片
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索</span>
            {hotKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setKeyword(item);
                  navigate(`/search?q=${encodeURIComponent(item)}`);
                }}
                className="px-2.5 py-1 rounded-md bg-background-100 border border-background-300/60 text-[11px] text-foreground-600 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                {item}
              </button>
            ))}
          </div>

          {query ? (
            <div className="mt-10">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-background-300/60">
                <p className="text-sm text-foreground-600">
                  关键词「<span className="text-accent-500">{query}</span>」共找到{" "}
                  <span className="text-foreground-950">{results.length}</span> 个结果
                </p>
                <Link
                  to="/category/popular"
                  className="text-xs text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览热门推荐
                </Link>
              </div>

              {results.length > 0 ? (
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-5">
                  {results.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="mt-8 rounded-lg border border-background-300/60 bg-background-100 px-6 py-16 text-center">
                  <span className="w-14 h-14 mx-auto rounded-full bg-background-200/70 flex items-center justify-center text-xl text-foreground-500">
                    <i className="ri-file-search-line" />
                  </span>
                  <h2 className="mt-5 text-base text-foreground-900">没有找到相关影片</h2>
                  <p className="mt-2 text-xs md:text-sm text-foreground-500 max-w-[420px] mx-auto leading-relaxed">
                    试试更换关键词，或使用更短的片名与题材标签。你也可以直接浏览热门排行。
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                    <Link
                      to="/category/popular"
                      className="h-10 px-5 rounded-md bg-accent-500 text-background-50 text-xs flex items-center justify-center cursor-pointer hover:bg-accent-600 transition-colors whitespace-nowrap"
                    >
                      查看热门影片
                    </Link>
                    <Link
                      to="/"
                      className="h-10 px-5 rounded-md border border-background-400/60 text-foreground-800 text-xs flex items-center justify-center cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
                    >
                      返回首页
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-8">
              <div className="rounded-lg border border-background-300/60 bg-background-100 px-6 py-14 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200/70 flex items-center justify-center text-xl text-foreground-500">
                  <i className="ri-movie-2-line" />
                </span>
                <h2 className="mt-5 text-base text-foreground-900">输入关键词开始检索</h2>
                <p className="mt-2 text-xs md:text-sm text-foreground-500">
                  片库共收录 {movies.length} 部演示影片，试试搜索「文艺」或「韩国」。
                </p>
              </div>
              <div className="rounded-lg border border-background-300/60 bg-background-100 p-4 md:p-5">
                <h3 className="text-sm text-foreground-900">试试这些分类</h3>
                <div className="mt-3 flex flex-col gap-2">
                  {[
                    { label: "经典典藏", to: "/category/classic" },
                    { label: "近期新片", to: "/category/recent" },
                    { label: "热门排行", to: "/category/popular" },
                    { label: "多元题材", to: "/category/themes" },
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center justify-between px-3 py-2.5 rounded-md bg-background-50 border border-background-300/50 text-xs text-foreground-700 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer"
                    >
                      {item.label}
                      <i className="ri-arrow-right-s-line" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}